/**
 * AppLockGate — wraps the signed-in app shell. When enforced and locked it
 * renders ONLY the LockScreen (children unmounted, so no data fetches behind
 * it). Enforced only for the installed PWA on a device with a usable biometric/
 * passcode authenticator (otherwise no lock — degrading to constant sign-outs
 * would be worse than the risk). Locks on cold launch (if >5 min since last
 * activity) and after 5 minutes idle. See appLock.ts + risk assessment R11.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { auth } from '../lib/firebase';
import { LockScreen } from './LockScreen';
import {
  IDLE_MS,
  isStandalone,
  platformAuthAvailable,
  readLastActive,
  writeLastActive,
} from './appLock';

export function AppLockGate({ children }: { children: ReactNode }) {
  const { signOut } = useAuth();
  const email = auth.currentUser?.email ?? '';

  // Sync-decidable up front: a normal browser tab is never enforced (no flash).
  // Standalone starts as `null` (pending the async platform-auth check).
  const [enforced, setEnforced] = useState<boolean | null>(() => (isStandalone() ? null : false));
  const [locked, setLocked] = useState<boolean>(() => {
    if (!isStandalone()) return false;
    const last = readLastActive();
    return !last || Date.now() - last > IDLE_MS; // lock immediately on a stale cold start
  });

  // Confirm the device can actually do biometric/passcode. If not, don't lock.
  useEffect(() => {
    if (!isStandalone()) return;
    let alive = true;
    void platformAuthAvailable().then((ok) => {
      if (!alive) return;
      setEnforced(ok);
      if (!ok) setLocked(false); // can't biometric → don't trap the user
    });
    return () => {
      alive = false;
    };
  }, []);

  // Idle + visibility watchdog while enforced and currently unlocked.
  useEffect(() => {
    if (enforced !== true || locked) return;
    let timer = 0;
    const lock = () => setLocked(true);
    const bump = () => {
      writeLastActive(Date.now());
      window.clearTimeout(timer);
      timer = window.setTimeout(lock, IDLE_MS);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        writeLastActive(Date.now());
        return;
      }
      if (Date.now() - readLastActive() > IDLE_MS) lock();
      else bump();
    };
    let lastMove = 0;
    const onMove = () => {
      const n = Date.now();
      if (n - lastMove > 15_000) {
        lastMove = n;
        bump();
      }
    };
    const tapEvents: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'touchstart'];
    tapEvents.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    window.addEventListener('pointermove', onMove, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);
    bump(); // start the countdown
    return () => {
      window.clearTimeout(timer);
      tapEvents.forEach((e) => window.removeEventListener(e, bump));
      window.removeEventListener('pointermove', onMove);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enforced, locked]);

  // Show the lock while it's engaged (children stay unmounted → no data leak).
  if (locked && enforced !== false) {
    return (
      <LockScreen
        email={email}
        onUnlocked={() => {
          writeLastActive(Date.now());
          setLocked(false);
        }}
        onSignOut={() => void signOut()}
      />
    );
  }
  return <>{children}</>;
}
